import { Link } from "react-router-dom";
import { DB } from "@/lib/media";
import { trackWhatsAppClick } from "@/lib/analytics";
import { clearConsent } from "@/lib/consent";
import {
    WHATSAPP_HREF,
    EMAIL_HREF,
    STUDIO_EMAIL,
    SOCIALS,
} from "@/lib/links";

const NAV_LINKS = [
    { to: "/services", label: "Services" },
    { to: "/work", label: "Work" },
    { to: "/about", label: "About" },
    { to: "/contact", label: "Contact" },
];

const SOCIAL_LINKS = [
    { label: "Instagram", href: SOCIALS.instagram, key: "instagram" },
    { label: "LinkedIn", href: SOCIALS.linkedin, key: "linkedin" },
    { label: "Facebook", href: SOCIALS.facebook, key: "facebook" },
];

export default function Footer() {
    return (
        <footer
            data-testid="site-footer"
            className="bg-obsidian text-bone relative pt-20 pb-8 grain overflow-hidden"
        >
            <div className="mx-auto max-w-[1400px] px-6 lg:px-10 relative z-10">
                <div className="grid grid-cols-12 gap-y-14 lg:gap-x-10">
                    {/* Brand block */}
                    <div className="col-span-12 lg:col-span-5">
                        <img
                            src={DB.logoWhite}
                            alt="Digitalbeej"
                            className="h-24 lg:h-28 w-auto object-contain opacity-90"
                            loading="lazy"
                            decoding="async"
                        />
                        <p className="mt-10 lg:mt-14 font-serif italic text-2xl lg:text-3xl text-bone/80 leading-snug max-w-md">
                            Building brands from seed to scale.
                        </p>
                        <p className="mt-6 text-sm text-bone/60 leading-relaxed max-w-md">
                            A modern creative studio combining branding,
                            websites, AI, performance marketing and commerce
                            growth.
                        </p>

                        <a
                            data-testid="footer-cta-whatsapp"
                            href={WHATSAPP_HREF}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() =>
                                trackWhatsAppClick({
                                    section: "footer",
                                    buttonName: "footer_whatsapp",
                                })
                            }
                            className="mt-8 group inline-flex items-center gap-3 bg-bone text-obsidian px-6 py-3.5 text-xs uppercase tracking-[0.2em] font-bold hover:bg-olive transition-colors duration-300"
                        >
                            <span
                                aria-hidden
                                className="inline-block w-1.5 h-1.5 bg-[#0A3F2C] rounded-full"
                            />
                            Start on WhatsApp
                            <span
                                aria-hidden
                                className="transition-transform duration-300 group-hover:translate-x-1"
                            >
                                →
                            </span>
                        </a>
                    </div>

                    {/* Nav column */}
                    <div className="col-span-6 lg:col-span-2">
                        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-bone/45 mb-5">
                            Explore
                        </div>
                        <ul className="space-y-3">
                            {NAV_LINKS.map((l) => (
                                <li key={l.to}>
                                    <Link
                                        to={l.to}
                                        data-testid={`footer-nav-${l.to.slice(1)}`}
                                        className="text-base link-underline"
                                    >
                                        {l.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Contact column */}
                    <div className="col-span-6 lg:col-span-2">
                        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-bone/45 mb-5">
                            Contact
                        </div>
                        <ul className="space-y-3 text-sm">
                            <li>
                                <a
                                    href={EMAIL_HREF}
                                    data-testid="footer-email"
                                    className="link-underline"
                                >
                                    {STUDIO_EMAIL}
                                </a>
                            </li>
                            <li>
                                <a
                                    href={WHATSAPP_HREF}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="link-underline"
                                >
                                    +91 89288 64286
                                </a>
                            </li>
                            <li className="text-bone/70 pt-1">Mumbai · India</li>
                        </ul>
                    </div>

                    {/* Social column */}
                    <div className="col-span-12 lg:col-span-3">
                        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-bone/45 mb-5">
                            Follow
                        </div>
                        <ul className="space-y-3">
                            {SOCIAL_LINKS.map((s) => (
                                <li key={s.key}>
                                    <a
                                        href={s.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid={`footer-social-${s.key}`}
                                        className="text-base link-underline"
                                    >
                                        {s.label} ↗
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* Bottom strip */}
                <div className="mt-16 lg:mt-24 pt-8 border-t border-bone/15 flex flex-col gap-6 text-xs uppercase tracking-[0.2em] font-bold text-bone/55">
                    {/* Legal nav */}
                    <nav
                        data-testid="footer-legal-nav"
                        className="flex flex-wrap items-center gap-x-8 gap-y-3"
                    >
                        <Link
                            data-testid="footer-link-privacy"
                            to="/privacy"
                            className="hover:text-olive-mint transition-colors duration-200"
                        >
                            Privacy Policy
                        </Link>
                        <Link
                            data-testid="footer-link-terms"
                            to="/terms"
                            className="hover:text-olive-mint transition-colors duration-200"
                        >
                            Terms &amp; Conditions
                        </Link>
                        <button
                            data-testid="footer-cookie-preferences"
                            onClick={clearConsent}
                            className="uppercase tracking-[0.2em] font-bold text-bone/55 hover:text-olive-mint transition-colors duration-200"
                        >
                            Cookie preferences
                        </button>
                    </nav>

                    {/* Copyright + tagline */}
                    <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                        <div>© {new Date().getFullYear()} Digitalbeej Studio</div>
                        <div className="flex items-center gap-6">
                            <span>From seed to scale</span>
                            <span className="text-olive">●</span>
                            <span>Made in Mumbai</span>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
}
